// Lib
import React from 'react';
import PropTypes from 'prop-types';
import { throttle, debounce } from 'lodash';

// Prop comparisons
import { noLonger, now } from '../../utils/react/propsComparisons';
import { getHasImageToShow } from './imageElementUtils';
import { isElementLocked } from '../../../common/elements/utils/elementPropertyUtils';
import { DROP_TARGET_TYPES } from '../../../common/elements/elementConstants';

const isNowHovered = now('isHovered');
const canNowDrop = now('canDrop');
const isNoLongerHovered = noLonger('isHovered');

const HOVER_DELAY_DEFAULT = 500;

export const imageHoverWatcher = (DecoratedComponent) => {
    class ImageHoverWatcher extends React.Component {
        componentWillReceiveProps(nextProps) {
            if (isNoLongerHovered(this.props, nextProps)) {
                this.disableDropTarget();
            }

            if (isNowHovered(this.props, nextProps)) {
                // prevent image replacement when hovering over locked image
                if (isElementLocked(this.props.element)) return;

                // If it's a placeholder image, just ignore this
                if (!getHasImageToShow(nextProps)) {
                    const { setParentHoveredChildAcceptsDrop, canDrop } = nextProps;

                    // Drag and Drop - Shallow hover hack
                    // This will turn off a list's drop preview if the image is going to accept the drop
                    setParentHoveredChildAcceptsDrop && setParentHoveredChildAcceptsDrop(canDrop);

                    return;
                }

                this.debouncedEnableDropTarget();

                document.addEventListener('dragover', this.throttledOnDragOver);
            }

            if (!getHasImageToShow(nextProps)) return;

            if (canNowDrop(this.props, nextProps)) {
                this.forcedHoverEffect = true;
                this.props.dispatchForceHoverEffect(DROP_TARGET_TYPES.IMAGE);
            }
        }

        componentWillUnmount() {
            this.disableDropTarget();
        }

        onDragOver = (e) => {
            if (e.clientX === this.prevClientX && e.clientY === this.prevClientY) return;

            this.prevClientX = e.clientX;
            this.prevClientY = e.clientY;

            this.debouncedEnableDropTarget();
        };

        throttledOnDragOver = throttle(this.onDragOver, 20);

        enableDropTarget = () => {
            const { setParentHoveredChildAcceptsDrop } = this.props;

            this.props.setAllowDrop(true);

            // Drag and Drop - Shallow hover hack
            // This will turn off a list's drop preview if the image is going to accept the drop
            setParentHoveredChildAcceptsDrop && setParentHoveredChildAcceptsDrop(this.props.canDrop);

            this.throttledOnDragOver.cancel();
            document.removeEventListener('dragover', this.throttledOnDragOver);
        };

        debouncedEnableDropTarget = debounce(this.enableDropTarget, HOVER_DELAY_DEFAULT);

        disableDropTarget = () => {
            const { setParentHoveredChildAcceptsDrop } = this.props;

            this.throttledOnDragOver.cancel();
            document.removeEventListener('dragover', this.throttledOnDragOver);

            this.debouncedEnableDropTarget.cancel();

            this.props.setAllowDrop(false);

            // Drag and Drop - Shallow hover hack
            // This will turn off a list's drop preview if the image is going to accept the drop
            setParentHoveredChildAcceptsDrop && setParentHoveredChildAcceptsDrop(false);

            if (this.forcedHoverEffect) {
                this.forcedHoverEffect = false;
                this.props.dispatchClearHoverEffect();
            }
        };

        render() {
            return <DecoratedComponent {...this.props} />;
        }
    }

    ImageHoverWatcher.propTypes = {
        element: PropTypes.object,
        placeholder: PropTypes.object,
        setAllowDrop: PropTypes.func,
        canDrop: PropTypes.bool,
        dispatchForceHoverEffect: PropTypes.func,
        dispatchClearHoverEffect: PropTypes.func,

        setParentHoveredChildAcceptsDrop: PropTypes.func,
    };

    return ImageHoverWatcher;
};
